import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Client for the organisation API on dashboard2.
 *
 * Written against `outputs/PubQ-Organisation-API-Contract.md` (first version,
 * 2026-10-21), which describes the backend as it is live on staging. Base URL is
 * `environment.pubQueueApiUrlEU + '/v1'`; every call carries the signed-in
 * user's Firebase ID token, which the contract requires on all of `/v1/org/*`
 * (a bare uid is refused).
 *
 * Conventions taken from the contract and relied on here:
 *  - statistics money is MAJOR units with a `currencyCode`; catalogue prices are
 *    integer MINOR units (`priceMinor`), the same as `menuItems.Cost`;
 *  - nothing is ever summed across currencies — a multi-currency org returns one
 *    row per currency, and the UI keeps them apart;
 *  - the caller may NARROW a unit set but never widen it, so the server decides
 *    what "this organisation" means;
 *  - org access is an explicit grant. `UserOrganisation` membership is not one.
 */

// ---------------------------------------------------------------------------
// §2 Identity & organisation
// ---------------------------------------------------------------------------

export interface OrgMineEntry {
    organisationId: number;
    name: string;
    orgRoute: string | null;
    grantedAtUtc: string | null;
    unitCount: number;
}

/** `GET /org/mine` — the gate for the whole area. */
export interface OrgMine {
    /** PubQ staff (custom claim `pubqSupport`): may open any org, and grant access. */
    support: boolean;
    organisations: OrgMineEntry[];
}

export interface OrgLegalEntity {
    clientId: string;
    name: string;
    vatNo: string | null;
    corporateNo: string | null;
    countries: string[];
    unitCount: number;
}

export interface OrgOverview {
    organisationId: number;
    name: string;
    unitCount: number;
    countries: string[];
    currencies: string[];
    /** A group is several legal entities under one organisation; staging org 2 has 8. */
    legalEntities: OrgLegalEntity[];
    callerIsSupport: boolean;
}

export interface OrgUnit {
    salesLocationId: string;
    routeName: string;
    name: string;
    clientId: string | null;
    legalEntityName: string | null;
    vatNo?: string | null;
    corporateNo?: string | null;
    countryCode: string | null;
    city?: string | null;
    currencyCode: string | null;
    timeZone?: string | null;
}

// ---------------------------------------------------------------------------
// §3 Org admins
// ---------------------------------------------------------------------------

export interface OrgAdmin {
    uid: string;
    email: string | null;
    displayName: string | null;
    grantedBy: string | null;
    grantedAtUtc: string | null;
    reason: string | null;
}

export interface OrgAdminAudit {
    uid: string;
    role: string;
    action: 'grant' | 'revoke';
    actorUid: string;
    atUtc: string;
    reason: string | null;
}

// ---------------------------------------------------------------------------
// §4 Statistics
// ---------------------------------------------------------------------------

/** Every stats response shares this envelope. */
export interface OrgStatsEnvelope<T> {
    organisationId: number;
    dateFrom: string;
    dateTo: string;
    unitCount: number;
    data: T;
}

export interface OrgCurrencyTotal {
    currencyCode: string;
    receiptCount: number;
    totalAmount: number;
    netAmount: number;
    vatAmount: number;
    discountAmount: number;
    averageReceipt: number | null;
    /** Receipts that are not sales (Type != 0). Reported, never mixed in. */
    otherTypeCount: number;
    otherTypeAmount: number;
    activeUnits: number;
}

export interface OrgUnitSales {
    currencyCode: string;
    receiptCount: number;
    totalAmount: number;
    netAmount: number;
}

export interface OrgSummaryUnit {
    salesLocationId: string;
    routeName: string;
    name: string;
    clientId: string | null;
    legalEntityName: string | null;
    countryCode: string | null;
    /** Empty for a unit with no sales in the period — the unit is still listed. */
    sales: OrgUnitSales[];
}

export interface OrgSummary {
    totals: OrgCurrencyTotal[];
    units: OrgSummaryUnit[];
}

export interface OrgDayRow {
    date: string;
    currencyCode: string;
    receiptCount: number;
    totalAmount: number;
    netAmount: number;
    salesLocationId?: string;
}

export interface OrgHourRow {
    hour: number;
    currencyCode: string;
    receiptCount: number;
    totalAmount: number;
}

export interface OrgPaymentRow {
    description: string;
    currencyCode: string;
    paymentCount: number;
    amount: number;
}

export interface OrgProductStatRow {
    articleId: string | null;
    description: string;
    currencyCode: string;
    quantity: number;
    totalAmount: number;
    /** How many of the org's units sold it. */
    unitCount: number;
}

// ---------------------------------------------------------------------------
// §5 Catalogue
// ---------------------------------------------------------------------------

export interface OrgProductType {
    productTypeId: number;
    containsAlcohol: boolean;
    [k: string]: unknown;
}

export interface OrgProduct {
    productId: number;
    organisationId: number;
    /** The same key in every unit's menuItems. */
    itemKey: string | null;
    name: string;
    nameInternal: string | null;
    description: string | null;
    category: string | null;
    /** Integer minor units. */
    priceMinor: number;
    priceConfig: { [channel: string]: number } | null;
    vatPercent: number;
    takeawayVatPercent: number | null;
    vatAccountMatch: { accountNumber?: string; name?: string; externalId?: string } | null;
    productType: OrgProductType | null;
    /**
     * The product's id in an external system — Compass Group's TCPOS article
     * number, in the case this was added for. Published to each unit as
     * `menuItems.externalOrigin = { key: externalIdKey, externalId }`, which is
     * the field the TCPOS sales export and the unit product editor already read.
     * `""`/null clears it, and clearing removes only the org's value.
     */
    externalId: string | null;
    /** Defaults to `TCPOS` when an externalId is set. */
    externalIdKey: OrgExternalIdKey | null;
    /**
     * Firebase Storage download URL for the product picture, projected onto each
     * unit's `menuItems` image field on publish. `null` clears it.
     */
    imageUrl: string | null;
    /** BlurHash placeholder for `imageUrl`; sent and cleared together with it. */
    imageBlurHash: string | null;
    enabled: boolean;
    archived: boolean;
    version: number;
    updatedBy: string | null;
    updatedAtUtc: string | null;
    assignedUnits: number;
    publishedUnits: number;
}

/** The keys the unit product editor offers, per the contract. */
export type OrgExternalIdKey = 'TCPOS' | 'Item ID' | 'External Id';

export interface OrgProductAssignment {
    salesLocationId: string;
    enabled: boolean;
    priceMinorOverride: number | null;
    articleAccountIdOverride?: number | null;
    /** For a site whose TCPOS article numbers differ from the group's. */
    externalIdOverride?: string | null;
    publishedVersion: number | null;
    publishedAtUtc: string | null;
    lastError: string | null;
    upToDate: boolean;
}

export interface OrgProductDetail extends OrgProduct {
    units: OrgProductAssignment[];
}

export interface OrgVatAccount {
    articleAccountId: number;
    name: string;
    vatPercent: number;
    type: string | null;
    salesAccountNumber: string | null;
    externalId: string | null;
}

// ---------------------------------------------------------------------------
// §6 Codes & offers
// ---------------------------------------------------------------------------

export type OrgOfferKind = 'code' | 'offer';

export interface OrgOffer {
    offerId: number;
    organisationId: number;
    itemKey: string | null;
    kind: OrgOfferKind;
    title: string;
    description: string | null;
    /** Required for `code`; upper-case, unique among the org's active codes. */
    code: string | null;
    /** (0, 100] — percent only, because that is all the apps implement. */
    discountPercent: number;
    validFrom: string | null;
    validTo: string | null;
    definition: { [k: string]: unknown } | null;
    enabled: boolean;
    archived: boolean;
    liveNow: boolean;
    version: number;
    assignedUnits: number;
    liveUnits: number;
}

export interface OrgOfferAssignment {
    salesLocationId: string;
    enabled: boolean;
    discountPercentOverride: number | null;
    publishedState: 'live' | 'withdrawn' | null;
    publishedVersion: number | null;
    publishedAtUtc: string | null;
    lastError: string | null;
}

export interface OrgOfferDetail extends OrgOffer {
    units: OrgOfferAssignment[];
}

// ---------------------------------------------------------------------------
// §7 Publishing
// ---------------------------------------------------------------------------

export type OrgPublishKind = 'products' | 'offers';

export interface OrgPublishDryRun {
    dryRun: true;
    kind: OrgPublishKind;
    units: number;
    wouldWrite: number;
    unchanged: number;
    wouldWithdraw: number;
    wouldFail: number;
    warnings: number;
    orphanUnits: string[];
    report: {
        salesLocationId: string;
        routeName: string;
        name: string;
        results: { itemKey: string; itemId?: number; outcome: string; detail: string }[];
    }[];
}

export interface OrgPublishRun {
    runId: number;
    organisationId: number;
    kind: OrgPublishKind;
    status: 'queued' | 'running' | 'done' | 'partial' | 'failed';
    scope: { itemIds: number[]; salesLocationIds: string[] };
    requestedBy: string | null;
    requestedAtUtc: string | null;
    finishedAtUtc: string | null;
    unitsTotal: number;
    unitsDone: number;
    written: number;
    unchanged: number;
    withdrawn: number;
    failed: number;
    warnings: number;
    message: string | null;
    enqueued?: boolean;
}

// ---------------------------------------------------------------------------

const REQUEST_TIMEOUT_MS = 30000;

/** A typed failure, so screens can tell "no access" from "not built yet". */
export class OrgApiError extends Error {
    constructor(readonly status: number, readonly code: string, message: string) {
        super(message);
    }
    /** The endpoint is not deployed in this environment. */
    get missing(): boolean {
        return this.status === 404 && this.code === 'not_deployed';
    }
    get forbidden(): boolean {
        return this.status === 403 || this.status === 401;
    }
}

@Injectable({ providedIn: 'root' })
export class OrgApiService {
    constructor(private http: HttpClient, private afAuth: AngularFireAuth) {}

    get configured(): boolean {
        return !!environment.pubQueueApiUrlEU;
    }

    // -- §2 identity ---------------------------------------------------------

    mine() {
        return this.call<OrgMine>('GET', '/org/mine');
    }
    overview(orgId: number) {
        return this.call<OrgOverview>('GET', `/org/${orgId}`);
    }
    units(orgId: number) {
        return this.call<{ organisationId: number; units: OrgUnit[] }>('GET', `/org/${orgId}/units`);
    }
    vatAccounts(orgId: number, salesLocationId: string) {
        return this.call<{ salesLocationId: string; routeName: string; accounts: OrgVatAccount[] }>(
            'GET', `/org/${orgId}/units/${encodeURIComponent(salesLocationId)}/vat-accounts`);
    }

    // -- §3 admins -----------------------------------------------------------

    admins(orgId: number) {
        return this.call<{ organisationId: number; role: string; admins: OrgAdmin[] }>('GET', `/org/${orgId}/admins`);
    }
    adminAudit(orgId: number, limit = 200) {
        return this.call<{ entries: OrgAdminAudit[] }>('GET', `/org/${orgId}/admins/audit`, { params: { limit } });
    }
    /** Staff only, per the contract. The screen hides this unless `support`. */
    grantAdmin(orgId: number, body: { email?: string; uid?: string; reason?: string }) {
        return this.call<{ uid: string; email: string | null; organisationId: number; role: string; created: boolean }>(
            'POST', `/org/${orgId}/admins`, { body });
    }
    revokeAdmin(orgId: number, uid: string, reason?: string) {
        return this.call<{ uid: string; organisationId: number; role: string; revoked: boolean }>(
            'DELETE', `/org/${orgId}/admins/${encodeURIComponent(uid)}`, { params: reason ? { reason } : undefined });
    }

    // -- §4 statistics -------------------------------------------------------

    statsSummary(orgId: number, dateFrom: string, dateTo: string, units?: string[]) {
        return this.call<OrgStatsEnvelope<OrgSummary>>('GET', `/org/${orgId}/stats/summary`, { params: this.range(dateFrom, dateTo, units) });
    }
    statsDayByDay(orgId: number, dateFrom: string, dateTo: string, perUnit = false, units?: string[]) {
        const params: any = this.range(dateFrom, dateTo, units);
        if (perUnit) params.perUnit = 'true';
        return this.call<OrgStatsEnvelope<OrgDayRow[]>>('GET', `/org/${orgId}/stats/daybyday`, { params });
    }
    statsHourByHour(orgId: number, dateFrom: string, dateTo: string, units?: string[]) {
        return this.call<OrgStatsEnvelope<OrgHourRow[]>>('GET', `/org/${orgId}/stats/hourbyhour`, { params: this.range(dateFrom, dateTo, units) });
    }
    statsPayments(orgId: number, dateFrom: string, dateTo: string, units?: string[]) {
        return this.call<OrgStatsEnvelope<OrgPaymentRow[]>>('GET', `/org/${orgId}/stats/payments`, { params: this.range(dateFrom, dateTo, units) });
    }
    statsProducts(orgId: number, dateFrom: string, dateTo: string, limit = 200, units?: string[]) {
        return this.call<OrgStatsEnvelope<OrgProductStatRow[]>>('GET', `/org/${orgId}/stats/products`, { params: { ...this.range(dateFrom, dateTo, units), limit } });
    }

    // -- §5 catalogue --------------------------------------------------------

    products(orgId: number, includeArchived = false) {
        return this.call<{ organisationId: number; products: OrgProduct[] }>(
            'GET', `/org/${orgId}/products`, { params: includeArchived ? { includeArchived: 'true' } : undefined });
    }
    product(orgId: number, productId: number) {
        return this.call<OrgProductDetail>('GET', `/org/${orgId}/products/${productId}`);
    }
    createProduct(orgId: number, body: Partial<OrgProduct>) {
        return this.call<OrgProductDetail>('POST', `/org/${orgId}/products`, { body });
    }
    updateProduct(orgId: number, productId: number, body: Partial<OrgProduct>) {
        return this.call<OrgProduct>('PATCH', `/org/${orgId}/products/${productId}`, { body });
    }
    archiveProduct(orgId: number, productId: number) {
        return this.call<OrgProduct>('DELETE', `/org/${orgId}/products/${productId}`);
    }
    setProductUnits(orgId: number, productId: number, units: {
        salesLocationId: string;
        enabled?: boolean;
        priceMinorOverride?: number | null;
        articleAccountIdOverride?: number | null;
        externalIdOverride?: string | null;
    }[]) {
        return this.call<OrgProductDetail>('PUT', `/org/${orgId}/products/${productId}/units`, { body: { units } });
    }

    // -- §6 offers -----------------------------------------------------------

    offers(orgId: number, includeArchived = false) {
        return this.call<{ organisationId: number; offers: OrgOffer[] }>(
            'GET', `/org/${orgId}/offers`, { params: includeArchived ? { includeArchived: 'true' } : undefined });
    }
    offer(orgId: number, offerId: number) {
        return this.call<OrgOfferDetail>('GET', `/org/${orgId}/offers/${offerId}`);
    }
    createOffer(orgId: number, body: Partial<OrgOffer>) {
        return this.call<OrgOfferDetail>('POST', `/org/${orgId}/offers`, { body });
    }
    updateOffer(orgId: number, offerId: number, body: Partial<OrgOffer>) {
        return this.call<OrgOffer>('PATCH', `/org/${orgId}/offers/${offerId}`, { body });
    }
    archiveOffer(orgId: number, offerId: number) {
        return this.call<OrgOffer>('DELETE', `/org/${orgId}/offers/${offerId}`);
    }
    setOfferUnits(orgId: number, offerId: number, units: {
        salesLocationId: string;
        enabled?: boolean;
        discountPercentOverride?: number | null;
    }[]) {
        return this.call<OrgOfferDetail>('PUT', `/org/${orgId}/offers/${offerId}/units`, { body: { units } });
    }

    // -- §7 publishing -------------------------------------------------------

    /** `dryRun: true` writes nothing and previews per unit — the fan-out preview. */
    publishDryRun(orgId: number, kind: OrgPublishKind, itemIds?: number[], salesLocationIds?: string[]) {
        return this.call<OrgPublishDryRun>('POST', `/org/${orgId}/publish`, {
            body: { kind, dryRun: true, ...(itemIds?.length ? { itemIds } : {}), ...(salesLocationIds?.length ? { salesLocationIds } : {}) },
        });
    }
    publish(orgId: number, kind: OrgPublishKind, itemIds?: number[], salesLocationIds?: string[]) {
        return this.call<OrgPublishRun>('POST', `/org/${orgId}/publish`, {
            body: { kind, dryRun: false, ...(itemIds?.length ? { itemIds } : {}), ...(salesLocationIds?.length ? { salesLocationIds } : {}) },
        });
    }
    publishRuns(orgId: number, limit = 50) {
        return this.call<{ runs: OrgPublishRun[] }>('GET', `/org/${orgId}/publish/runs`, { params: { limit } });
    }
    publishRun(orgId: number, runId: number) {
        return this.call<OrgPublishRun & { items: { salesLocationId: string; itemKey: string; outcome: string; detail: string; atUtc: string }[] }>(
            'GET', `/org/${orgId}/publish/runs/${runId}`);
    }
    retryRun(orgId: number, runId: number) {
        return this.call<OrgPublishRun>('POST', `/org/${orgId}/publish/runs/${runId}/retry`, { body: {} });
    }

    // -- plumbing ------------------------------------------------------------

    private range(dateFrom: string, dateTo: string, units?: string[]) {
        const p: any = { dateFrom, dateTo };
        // `units` narrows; the contract is explicit that it can never add units
        // outside the org, so sending it is safe but never a grant.
        if (units?.length) p.units = units.join(',');
        return p;
    }

    private async headers(): Promise<HttpHeaders> {
        const user = await this.afAuth.currentUser;
        if (!user) throw new OrgApiError(401, 'unauthenticated', 'Not signed in.');
        return new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` });
    }

    private async call<T>(
        method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
        path: string,
        opts: { body?: unknown; params?: Record<string, any> } = {},
    ): Promise<T> {
        if (!this.configured) throw new OrgApiError(0, 'not_configured', 'The dashboard API is not configured in this environment.');
        const url = `${environment.pubQueueApiUrlEU}/v1${path}`;
        let params = new HttpParams();
        for (const [k, v] of Object.entries(opts.params ?? {})) {
            if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
        }
        try {
            const headers = await this.headers();
            const req =
                method === 'GET' ? this.http.get<T>(url, { headers, params })
                : method === 'DELETE' ? this.http.delete<T>(url, { headers, params })
                : method === 'PUT' ? this.http.put<T>(url, opts.body ?? {}, { headers, params })
                : method === 'PATCH' ? this.http.patch<T>(url, opts.body ?? {}, { headers, params })
                : this.http.post<T>(url, opts.body ?? {}, { headers, params });
            return await firstValueFrom(req.pipe(timeout(REQUEST_TIMEOUT_MS)));
        } catch (err) {
            if (err instanceof OrgApiError) throw err;
            if (err instanceof HttpErrorResponse) {
                const code = err.error?.error || (err.status === 404 ? 'not_deployed' : 'internal');
                throw new OrgApiError(err.status, code, err.error?.message || err.message);
            }
            throw new OrgApiError(0, 'timeout', 'The organisation API did not answer in time.');
        }
    }
}
